import { useEffect, useMemo, useState } from "react";
import AppShell from "../shell/AppShell.jsx";
import WorkerRecordDrawer from "../workerRecord/WorkerRecordDrawer.jsx";
import { useBackofficeIdentity } from "../hierarchy/BackofficeIdentityContext.jsx";
import { useWorkerProfilesList, WORKER_PROFILES_DEFAULT_PAGE_SIZE } from "./useWorkerProfilesList.js";
import "./workerProfiles.css";
import "../pages/states.css";
import "../agent/buddyGuarantees/buddyGuarantees.css";
import Pagination from "../components/Pagination.jsx";

import PageSkeleton from "../components/Skeleton.jsx";
const CRUMBS = ["Backoffice", "SuperAdmin", "Worker Intake", "Worker Profiles"];
const SEARCH_DEBOUNCE_MS = 350;

// Wording rule for this screen (revised 2026-08-03 on the owner's note that it
// "ดูไม่มืออาชีพ"): an operator is never shown OUR plumbing. No "ไม่มีในข้อมูล
// ตอบกลับ API นี้", no "สงวนไว้" placeholders, no raw enum values, no column
// that reads the same on every single row. A thing either carries information
// or it is not on the page. Everything removed here was one of those - none of
// it was ever real data, so nothing true was lost. See the notes at each
// deletion site.

// Only WorkerStatus/ComplianceStatus value P097/P097R ever writes today -
// see WorkerProfileStatuses/WorkerComplianceStatuses on the backend. Not
// inventing "Active"/"Suspended" options that don't exist yet.
const WORKER_STATUS_OPTIONS = [{ value: "ProfileCreated", label: "สร้างโปรไฟล์แล้ว" }];
const COMPLIANCE_STATUS_OPTIONS = [{ value: "NotReadyForMatch", label: "รอ Compliance" }];

const COMPLIANCE_STATUS_LABELS = { NotReadyForMatch: "รอ Compliance" };

function complianceStatusLabel(code) {
  return COMPLIANCE_STATUS_LABELS[code] ?? code ?? "-";
}

function describeListError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์เข้าถึงรายการ Worker Profile นี้";
  }
  return "กรุณาลองรีเฟรชหน้านี้อีกครั้ง หากยังพบปัญหากรุณาติดต่อฝ่ายปฏิบัติการ";
}

/** Two-letter monogram from a Thai/English full name - never a fabricated photo. */
function monogram(fullName) {
  const trimmed = (fullName ?? "").trim();
  if (!trimmed) {
    return "-";
  }
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

/**
 * /backoffice/worker-intake/worker-profiles - the Worker Profile inbox
 * (Prompt 098B, built from the W46 reference). Read-only over the Prompt
 * 097/097R API: GET /api/backoffice/worker-profiles (list) and
 * GET /api/backoffice/worker-profiles/{workerId} (detail) only. There is no
 * create action anywhere on this page - a Worker Profile is only ever
 * created from the approved e-KYC review flow (a different screen), and
 * this file never calls POST .../worker-profile, the e-KYC detail API, the
 * invite-card API, or any matching/tracking/buddy API (none of the last
 * three exist yet).
 *
 * REVISED 2026-08-03. Every KPI, column and filter the backend has no field
 * for used to render as an explicit "สงวนไว้" placeholder naming the missing
 * API. That was honest but unprofessional to look at - half the KPI row, three
 * of nine table columns and two filters were telling the operator about our
 * backlog instead of about their workers. Those surfaces are now simply absent.
 *
 * The honesty rule is UNCHANGED and still absolute: nothing on this page is
 * fabricated, no sample row, no invented number. What changed is that a fact we
 * do not have is now shown by not showing it, rather than by printing the name
 * of the endpoint that lacks it. Anything genuinely derived-but-narrower (the
 * Compliance tile counts only the loaded page) still says so in plain words.
 */
export default function WorkerProfilesPage() {
  const { data: identity } = useBackofficeIdentity();

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [workerStatusFilter, setWorkerStatusFilter] = useState("");
  const [complianceStatusFilter, setComplianceStatusFilter] = useState("");
  const [nationalityFilter, setNationalityFilter] = useState("");
  const [agentFilter, setAgentFilter] = useState("");
  const [page, setPage] = useState(1);
  // Clicking a row opens the ONE shared worker record - this screen no longer
  // has a drawer of its own to keep in step with it.
  const [workerRecordId, setWorkerRecordId] = useState(null);

  // Debounce free-text search before it ever becomes part of the fetch
  // filters - typing does not fire one request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Any real filter change (not a page change) returns to page 1.
  useEffect(() => {
    setPage(1);
  }, [search, workerStatusFilter, complianceStatusFilter, nationalityFilter, agentFilter]);

  const filters = useMemo(
    () => ({
      page,
      pageSize: WORKER_PROFILES_DEFAULT_PAGE_SIZE,
      workerStatus: workerStatusFilter,
      complianceStatus: complianceStatusFilter,
      nationality: nationalityFilter,
      agentId: agentFilter,
      search,
    }),
    [page, workerStatusFilter, complianceStatusFilter, nationalityFilter, agentFilter, search]
  );

  const { status: listStatus, data: listData, error: listError, refetch: refetchList } = useWorkerProfilesList(filters);

  function handleRefresh() {
    refetchList();
  }

  const items = listData?.items ?? [];
  const summary = listData?.summary ?? null;
  const totalCount = listData?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / WORKER_PROFILES_DEFAULT_PAGE_SIZE));
  const hasActiveFilters = Boolean(search || workerStatusFilter || complianceStatusFilter || nationalityFilter || agentFilter);

  // "Pending Compliance" has no dedicated summary field on the P097/P097R
  // response (only profileCreatedCount/todayCount/byNationality/byAgent) -
  // this is a client-side-only count over whatever page of rows is
  // currently loaded, never claimed as a platform-wide total. Mirrors the
  // same honesty pattern EkycReviewQueuePage.jsx uses for its own two
  // derived-only KPI tiles.
  const pendingComplianceOnPage = items.filter((item) => item.complianceStatus === "NotReadyForMatch").length;

  const isInitialLoading = listStatus === "loading" && listData === null;
  const isInitialError = listStatus === "error" && listData === null;

  if (isInitialLoading) {
    return (
      <AppShell crumbs={CRUMBS}>
        <PageSkeleton variant="table" label="กำลังโหลด Worker Profiles..." />
    </AppShell>
    );
  }

  if (isInitialError) {
    return (
      <AppShell crumbs={CRUMBS}>
        <div className="wx-state-card wx-state-card--danger" role="alert">
          <h1 className="wx-state-title">ไม่สามารถโหลด Worker Profiles ได้</h1>
          <p className="wx-state-body">{describeListError(listError)}</p>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell crumbs={CRUMBS}>
      <div className="wx-wp-page">
        <header className="wx-wp-head">
          <div className="wx-wp-title">
            <h1>Worker Profiles</h1>
            {/* Was three sentences explaining which backend step creates a
                Worker Profile and why this screen has no Create button. An
                operator does not need our build order - they need to know what
                they are looking at. One sentence. */}
            <p>แรงงานที่ผ่าน e-KYC แล้วและมีแฟ้มประวัติในระบบ</p>
          </div>
          {identity?.primaryRole && (
            <span className="wx-wp-scope-pill">
              Scope <b>{identity.primaryRole}</b>
            </span>
          )}
        </header>

        {/* Three tiles, all real. There were six: "ทักษะยังไม่ครบ",
            "ยังไม่ตั้งค่า Buddy" and "Ready For Match" all rendered a dash and
            the word "สงวนไว้" every single time, one of them even announcing
            "ยังไม่มี Buddy API". Half the KPI row was telling the operator
            about our backlog. */}
        <div className="wx-wp-metrics">
          {/* Real: list.totalCount */}
          <MetricCard label="ทั้งหมด" value={totalCount} note="แรงงานที่มีแฟ้มประวัติ" />
          {/* Real: summary.profileCreatedCount - the only WorkerStatus value
              written today, so this equals totalCount until a real
              Active/Suspended lifecycle exists. The note no longer prints the
              raw enum ("Worker Status: ProfileCreated"). */}
          <MetricCard label="พร้อมใช้งาน" value={summary?.profileCreatedCount} note="สร้างแฟ้มประวัติเรียบร้อย" variant="green" />
          {/* Derived from the currently loaded page only - no dedicated
              summary field exists yet (see pendingComplianceOnPage above).
              Still said out loud, because a count that silently means
              something narrower than it looks is a different kind of lie. */}
          <MetricCard
            label="รอตรวจ Compliance"
            value={pendingComplianceOnPage}
            note="นับเฉพาะในหน้านี้"
            variant="derived"
          />
        </div>

        <div className="wx-wp-toolbar">
          <label className="wx-wp-search">
            <span className="wx-visually-hidden">ค้นหาชื่อแรงงาน เบอร์โทร รหัส Worker หรือเลขพาสปอร์ต (ปิดบัง)</span>
            <input
              type="search"
              placeholder="ค้นหาชื่อแรงงาน เบอร์โทร รหัส Worker หรือเลขพาสปอร์ต (ปิดบัง)"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
          </label>

          <div className="wx-wp-filters">
            <label className="wx-wp-filter">
              <span>สถานะ</span>
              <select value={workerStatusFilter} onChange={(event) => setWorkerStatusFilter(event.target.value)}>
                <option value="">ทั้งหมด</option>
                {WORKER_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="wx-wp-filter">
              <span>Compliance</span>
              <select value={complianceStatusFilter} onChange={(event) => setComplianceStatusFilter(event.target.value)}>
                <option value="">ทั้งหมด</option>
                {COMPLIANCE_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="wx-wp-filter">
              <span>สัญชาติ</span>
              <select value={nationalityFilter} onChange={(event) => setNationalityFilter(event.target.value)}>
                <option value="">ทั้งหมด</option>
                {(summary?.byNationality ?? []).map((entry) => (
                  <option key={entry.nationality} value={entry.nationality}>
                    {entry.nationality} ({entry.count})
                  </option>
                ))}
              </select>
            </label>

            <label className="wx-wp-filter">
              <span>Agent</span>
              <select value={agentFilter} onChange={(event) => setAgentFilter(event.target.value)}>
                <option value="">ทั้งหมด</option>
                {(summary?.byAgent ?? []).map((entry) => (
                  <option key={entry.agentId} value={entry.agentId}>
                    {entry.agentCode} · {entry.displayName}
                  </option>
                ))}
              </select>
            </label>

            {/* The W46 reference also drew "สายงาน" and "ทักษะ" filters. Both
                were rendered as permanently-disabled "สงวนไว้" dropdowns because
                this API offers no summary to build their options from - two
                controls that could never be used, on every visit. Removed
                rather than left greyed out; they come back the day the backend
                can populate them. */}
          </div>

          <div className="wx-wp-toolbar-actions">
            {/* wx-wp-refresh-button is also listed in DetailDrawer's
                selectableSelector below - without it, clicking Refresh
                while a row is open is an "outside click" as far as the
                drawer's own close-on-outside-click listener is concerned. */}
            <button type="button" className="wx-button wx-button--ghost wx-wp-refresh-button" onClick={handleRefresh}>
              Refresh
            </button>
            {/* A permanently-disabled Export button sat here. A button that can
                never be pressed is worse than no button - it reads as broken
                rather than as unbuilt. */}
          </div>
        </div>

        <section className="wx-panel wx-wp-list-panel">
          <div className="wx-wp-list-head">
            <h2 className="wx-panel-title">Worker Profiles ทั้งหมด</h2>
            <span className="wx-wp-list-count">
              {totalCount} รายการ{listStatus === "loading" && listData !== null ? " · กำลังโหลด..." : ""}
            </span>
          </div>

          {listStatus === "error" && listData !== null && (
            <p className="wx-wp-inline-error" role="alert">
              {describeListError(listError)}
            </p>
          )}

          {items.length === 0 ? (
            <p className="wx-empty-list">{hasActiveFilters ? "ไม่พบรายการที่ตรงกับตัวกรองหรือคำค้นหา" : "ยังไม่มี Worker Profile ในระบบ"}</p>
          ) : (
            <>
              <div className="wx-wp-table-wrap">
                <table className="wx-wp-table">
                  {/* Nine columns became six. "ทักษะ" and "ประวัติการทำงาน"
                      printed the words "ดูในรายละเอียด" on every row (neither
                      is on the list DTO), and "Buddy" printed a "สงวนไว้" badge
                      on every row. Three columns of identical text is not data
                      - it is width taken from the four columns that do carry
                      something, on a table an operator scans all day. */}
                  <colgroup>
                    <col style={{ width: "27%" }} />
                    <col style={{ width: "17%" }} />
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "26%" }} />
                    <col style={{ width: "14%" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>แรงงาน</th>
                      <th>เบอร์โทร</th>
                      <th>สัญชาติ / ภาษา</th>
                      <th>Agent / สายงาน</th>
                      <th>Compliance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => {
                      const fullName = item.worker?.fullNameNative || item.worker?.fullNameEnglish || "-";
                      return (
                        <tr key={item.workerId} className="wx-wp-row" onClick={() => setWorkerRecordId(item.workerId)}>
                          <td>
                            <div className="wx-wp-worker-cell">
                              <span className="wx-wp-avatar" aria-hidden="true">
                                {monogram(fullName)}
                              </span>
                              <div>
                                <span className="wx-wp-cell-strong">{fullName}</span>
                                <span className="wx-wp-cell-sub">{item.workerCode}</span>
                              </div>
                            </div>
                          </td>
                          <td>
                            {/* The "LINE/WA: สงวนไว้" sub-line under every
                                number is gone - it announced two channels this
                                platform does not have, once per row. */}
                            <span className="wx-wp-cell-strong">{item.worker?.phoneNumber ?? "-"}</span>
                          </td>
                          <td>
                            <span className="wx-wp-cell-strong">{item.worker?.nationality ?? "-"}</span>
                            <span className="wx-wp-cell-sub">{item.worker?.preferredLanguageCode ?? "-"}</span>
                          </td>
                          <td>
                            <span className="wx-wp-cell-strong">
                              {item.agent?.agentCode ?? "-"} · {item.agent?.displayName ?? "-"}
                            </span>
                            <span className="wx-wp-cell-sub">
                              {item.line?.superAgentName ?? "-"} · {item.line?.plcName ?? "-"}
                            </span>
                          </td>
                          <td>
                            <span className="wx-badge wx-badge--warning">{complianceStatusLabel(item.complianceStatus)}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="wx-wp-cards">
                {items.map((item) => (
                  <WorkerProfileCard key={item.workerId} item={item} onOpen={() => setWorkerRecordId(item.workerId)} />
                ))}
              </div>

              <Pagination
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </>
          )}
        </section>
      </div>

      
        <WorkerRecordDrawer
        open={workerRecordId !== null}
        workerId={workerRecordId}
        onClose={() => setWorkerRecordId(null)}
        selectableSelector="tr.wx-wp-row, .wx-wp-card"
      />
    </AppShell>
  );
}

// The `reserved` variant of this card - a dash plus a "สงวนไว้" pill - went
// with the three tiles that used it. A KPI tile exists to carry a number; one
// that structurally cannot is not a tile, and keeping the branch alive would
// invite the next person to add a fourth.
function MetricCard({ label, value, note, variant }) {
  const variantClass = variant ? ` wx-wp-metric--${variant}` : "";
  return (
    <div className={`wx-wp-metric${variantClass}`}>
      <span className="wx-wp-metric-label">{label}</span>
      <strong className="wx-wp-metric-value">{value ?? "-"}</strong>
      {note && <span className="wx-wp-metric-note">{note}</span>}
    </div>
  );
}

function WorkerProfileCard({ item, onOpen }) {
  const fullName = item.worker?.fullNameNative || item.worker?.fullNameEnglish || "-";
  return (
    <div className="wx-wp-card" onClick={onOpen}>
      <div className="wx-wp-card-top">
        <div className="wx-wp-worker-cell">
          <span className="wx-wp-avatar" aria-hidden="true">
            {monogram(fullName)}
          </span>
          <div>
            <strong>{fullName}</strong>
            <span>{item.workerCode}</span>
          </div>
        </div>
        <span className="wx-badge wx-badge--warning">{complianceStatusLabel(item.complianceStatus)}</span>
      </div>
      <div className="wx-wp-card-grid">
        <div>
          <label>เบอร์โทร</label>
          <strong>{item.worker?.phoneNumber ?? "-"}</strong>
        </div>
        <div>
          <label>สัญชาติ / ภาษา</label>
          <strong>
            {item.worker?.nationality ?? "-"} · {item.worker?.preferredLanguageCode ?? "-"}
          </strong>
        </div>
        <div>
          <label>Agent</label>
          <strong>
            {item.agent?.agentCode ?? "-"} · {item.agent?.displayName ?? "-"}
          </strong>
        </div>
        <div>
          <label>สายงาน</label>
          <strong>
            {item.line?.superAgentName ?? "-"} · {item.line?.plcName ?? "-"}
          </strong>
        </div>
        {/* The narrow-screen card carried the same two dead fields the table
            did - "ทักษะ: ดูในรายละเอียด" and "Buddy: สงวนไว้" - which cost a
            whole row of a card that has to work on a phone. */}
      </div>
    </div>
  );
}
